import {
    BadGatewayException,
    BadRequestException,
    ConflictException,
    ForbiddenException,
    GatewayTimeoutException,
    HttpException,
    InternalServerErrorException,
    MethodNotAllowedException,
    NotFoundException,
    ServiceUnavailableException,
    UnauthorizedException,
    UnprocessableEntityException,
} from "./exceptions/index.js";

export interface HttpClientConfig {
    baseUrl?: string;
    defaultHeaders?: HeadersInit;
    timeout?: number;
    /**
     * Custom fetch implementation (for testing)
     * @default global fetch
     */
    fetch?: typeof fetch;
    /**
     * Parse real HTTP status from error message
     * Useful for APIs that always return 500 with real status in message
     * @example Vacasa API: "method: get, url: ..., status: 404, message: ..."
     * @default false
     */
    parseStatusFromMessage?: boolean;
    /**
     * Whether code is running on server
     * Used to determine if Next.js cache options should be applied
     * @default true
     */
    isServer?: boolean;
}

export interface RequestConfig extends RequestInit {
    signal?: AbortSignal;
    timeout?: number;
    /**
     * Cache revalidation time in seconds
     * Only works on the server (Server Components, API Routes, Server Actions)
     * @default 0 - no caching
     * @example 60 - revalidate every 60 seconds
     * @example 0 - disable caching
     */
    revalidate?: number;
    /**
     * Cache tags for revalidation
     * Only works on the server (Server Components, API Routes, Server Actions)
     * @example ["products", "categories"]
     */
    tags?: Array<string>;
}

/**
 * HTTP Client for making API requests
 * Handles errors and converts HTTP status codes to Error
 * Supports AbortSignal for request cancellation (from QueryState)
 * Supports timeout for SSR scenarios
 */
class HttpClientClass {
    private readonly baseUrl: string;
    private readonly defaultHeaders: HeadersInit;
    private readonly defaultTimeout?: number;
    private readonly fetchImpl: typeof fetch;
    private readonly parseStatusFromMessage: boolean;
    private readonly isServerEnv: boolean;

    constructor(config: HttpClientConfig = {}) {
        this.baseUrl = config.baseUrl || "";
        this.defaultHeaders = config.defaultHeaders || {
            "Content-Type": "application/json",
        };
        this.defaultTimeout = config.timeout;
        this.parseStatusFromMessage = config.parseStatusFromMessage || false;
        this.isServerEnv = config.isServer !== undefined ? config.isServer : true;
        // Bind fetch to globalThis to preserve context
        this.fetchImpl = config.fetch ? config.fetch.bind(globalThis) : fetch.bind(globalThis);
    }

    /**
     * Build full URL from path
     */
    private buildUrl(path: string): string {
        if (path.startsWith("http://") || path.startsWith("https://")) {
            return path;
        }

        const base = this.baseUrl.endsWith("/") ? this.baseUrl.slice(0, -1) : this.baseUrl;
        const urlPath = path.startsWith("/") ? path : `/${path}`;

        return `${base}${urlPath}`;
    }

    /**
     * Merge default headers with request headers
     */
    private mergeHeaders(requestHeaders?: HeadersInit): HeadersInit {
        if (!requestHeaders) {
            return this.defaultHeaders;
        }

        const merged = new Headers(this.defaultHeaders);

        if (requestHeaders instanceof Headers) {
            requestHeaders.forEach((value, key) => {
                merged.set(key, value);
            });
        } else if (Array.isArray(requestHeaders)) {
            requestHeaders.forEach(([key, value]) => {
                merged.set(key, value);
            });
        } else {
            Object.entries(requestHeaders).forEach(([key, value]) => {
                merged.set(key, value);
            });
        }

        return merged;
    }

    /**
     * Build Next.js cache options from request config
     * Only works on the server, returns empty object on client
     */
    private buildNextCacheOptions(revalidate: number, tags?: Array<string>): {next?: {revalidate: number; tags?: Array<string>}} {
        if (!this.isServerEnv) {
            return {};
        }

        const nextOptions: {revalidate: number; tags?: Array<string>} = {
            revalidate,
        };

        if (tags && tags.length > 0) {
            nextOptions.tags = tags;
        }

        return {next: nextOptions};
    }

    /**
     * Map API errorCode field to HTTP status code
     */
    private parseStatusFromErrorCode(errorBody: unknown): number | null {
        if (!errorBody || typeof errorBody !== "object") {
            return null;
        }

        const errorCode = (errorBody as Record<string, unknown>).errorCode;

        if (typeof errorCode !== "string") {
            return null;
        }

        const errorCodeMap: Record<string, number> = {
            invalid_input_error: 400,
            not_found_error: 404,
            unavailable_stay_error: 409,
            checkout_expired_error: 422,
            payment_declined_error: 422,
            failed_quote_error: 422,
            unknown_business_error: 500,
        };

        return errorCodeMap[errorCode] ?? null;
    }

    /**
     * Parse real status code from error message
     * @example "method: get, url: ..., status: 404, message: ..." -> 404
     */
    private parseStatusFromErrorMessage(errorBody: unknown): number | null {
        if (!errorBody || typeof errorBody !== "object") {
            return null;
        }

        const errorObj = errorBody as Record<string, unknown>;
        const message = errorObj.message;

        if (typeof message !== "string") {
            return null;
        }

        // Try to find "status: XXX" pattern in message
        const statusMatch = message.match(/status:\s*(\d{3})/i);
        if (statusMatch && statusMatch[1]) {
            return parseInt(statusMatch[1], 10);
        }

        // Detect common error patterns
        const lowerMessage = message.toLowerCase();

        // Unauthorized/authentication errors
        if (
            lowerMessage.includes("invalid token") ||
            lowerMessage.includes("token expired") ||
            lowerMessage.includes("unauthorized") ||
            lowerMessage.includes("authentication failed")
        ) {
            return 401;
        }

        // Forbidden errors
        if (lowerMessage.includes("forbidden") || lowerMessage.includes("access denied")) {
            return 403;
        }

        // Not found errors
        if (lowerMessage.includes("not found")) {
            return 404;
        }

        return null;
    }

    /**
     * Create appropriate HttpException based on HTTP status code
     */
    private createHttpExceptionFromResponse(response: Response, errorBody?: unknown, cause?: Error): HttpException {
        let statusCode = response.status;

        if (errorBody) {
            // Always check errorCode first
            const errorCodeStatus = this.parseStatusFromErrorCode(errorBody);
            if (errorCodeStatus !== null) {
                statusCode = errorCodeStatus;
                // Fall back to parsing status from message string if enabled
            } else if (this.parseStatusFromMessage) {
                const parsedStatus = this.parseStatusFromErrorMessage(errorBody);
                if (parsedStatus !== null) {
                    statusCode = parsedStatus;
                }
            }
        }

        const message = response.statusText || `HTTP ${statusCode}`;

        switch (statusCode) {
            case 400:
                return new BadRequestException(message, errorBody, cause);
            case 401:
                return new UnauthorizedException(message, errorBody, cause);
            case 403:
                return new ForbiddenException(message, errorBody, cause);
            case 404:
                return new NotFoundException(message, errorBody, cause);
            case 405:
                return new MethodNotAllowedException(message, errorBody, cause);
            case 409:
                return new ConflictException(message, errorBody, cause);
            case 422:
                return new UnprocessableEntityException(message, errorBody, cause);
            case 500:
                return new InternalServerErrorException(message, errorBody, cause);
            case 502:
                return new BadGatewayException(message, errorBody, cause);
            case 503:
                return new ServiceUnavailableException(message, errorBody, cause);
            case 504:
                return new GatewayTimeoutException(message, errorBody, cause);
            default:
                // For unknown status codes, create generic HttpException
                return new HttpException(statusCode, message, errorBody, cause);
        }
    }

    /**
     * Make HTTP request with error handling
     */
    private async request<T>(url: string, config: RequestConfig = {}): Promise<T> {
        const {signal, timeout, headers, revalidate = 0, tags, ...fetchConfig} = config;

        // Create abort controller for timeout
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        let abortController: AbortController | undefined;
        let combinedSignal: AbortSignal | undefined = signal;

        const requestTimeout = timeout ?? this.defaultTimeout;
        if (requestTimeout && requestTimeout > 0) {
            abortController = new AbortController();
            timeoutId = setTimeout(() => {
                abortController?.abort();
            }, requestTimeout);

            // If there's already a signal, need to listen to both
            if (signal) {
                // Create a new controller that listens to both signals
                const controller = new AbortController();
                combinedSignal = controller.signal;

                // Abort if either signal is aborted
                const abortHandler = (): void => controller.abort();
                signal.addEventListener("abort", abortHandler, {once: true});
                abortController.signal.addEventListener("abort", abortHandler, {once: true});
            } else {
                combinedSignal = abortController.signal;
            }
        }

        // Build Next.js cache options (only works on server, returns empty object on client)
        const nextOptions = this.buildNextCacheOptions(revalidate, tags);

        // Build fetch options with Next.js cache options
        const fetchOptions: RequestInit = {
            ...fetchConfig,
            signal: combinedSignal,
            headers: this.mergeHeaders(headers),
            ...nextOptions,
        };

        try {
            const response = await this.fetchImpl(this.buildUrl(url), fetchOptions);

            // Handle non-OK responses
            if (!response.ok) {
                // Try to parse error body as JSON
                let errorBody: unknown;
                try {
                    const contentType = response.headers.get("content-type");
                    if (contentType?.includes("application/json")) {
                        errorBody = await response.json();
                    }
                } catch {
                    // Failed to parse JSON, errorBody remains undefined
                }

                // Convert HTTP error to HttpException and throw
                throw this.createHttpExceptionFromResponse(response, errorBody);
            }

            // Parse response
            const contentType = response.headers.get("content-type");
            if (contentType?.includes("application/json")) {
                return (await response.json()) as T;
            }

            if (contentType?.includes("text/")) {
                return (await response.text()) as T;
            }

            // Return empty object for no content
            if (response.status === 204) {
                return {} as T;
            }

            // Fallback to text
            return (await response.text()) as T;
        } finally {
            // Always clear timeout when request completes (success or error)
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }
    }

    /**
     * GET request
     */
    async get<T>(path: string, config?: RequestConfig): Promise<T> {
        return this.request<T>(path, {
            ...config,
            method: "GET",
        });
    }

    /**
     * POST request with JSON body
     */
    async post<T>(path: string, data?: unknown, config?: RequestConfig): Promise<T> {
        return this.request<T>(path, {
            ...config,
            method: "POST",
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    /**
     * POST request with URL-encoded form data
     * @param path - Request path
     * @param data - URLSearchParams or object to be converted to form data
     * @param config - Optional request configuration
     */
    async postFormData<T>(path: string, data: URLSearchParams | Record<string, string>, config?: RequestConfig): Promise<T> {
        const formData = data instanceof URLSearchParams ? data : new URLSearchParams(data);

        return this.request<T>(path, {
            ...config,
            method: "POST",
            headers: {
                ...this.mergeHeaders(config?.headers),
                "Content-Type": "application/x-www-form-urlencoded",
            },
            body: formData.toString(),
        });
    }

    /**
     * PUT request
     */
    async put<T>(path: string, data?: unknown, config?: RequestConfig): Promise<T> {
        return this.request<T>(path, {
            ...config,
            method: "PUT",
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    /**
     * PATCH request
     */
    async patch<T>(path: string, data?: unknown, config?: RequestConfig): Promise<T> {
        return this.request<T>(path, {
            ...config,
            method: "PATCH",
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    /**
     * DELETE request
     */
    async delete<T>(path: string, config?: RequestConfig): Promise<T> {
        return this.request<T>(path, {
            ...config,
            method: "DELETE",
        });
    }
}

export type HttpClientInstance = HttpClientClass;

/**
 * Factory function to create HTTP client instance
 */
export function HttpClient(config?: HttpClientConfig): HttpClientInstance {
    return new HttpClientClass(config);
}
